if (!customElements.get("sibling-quick-view")) {
  customElements.define(
    "sibling-quick-view",
    class SiblingQuickView extends Drawer {
      constructor() {
        super();

        this.mediaWrapEl = this.querySelector(".sibling-qv-media-wrap");
        this.mediaEl = this.querySelector(".sibling-qv-media");
        this.mediaPrevBtn = this.querySelector(".sibling-qv-media-prev");
        this.mediaNextBtn = this.querySelector(".sibling-qv-media-next");
        this.mediaDotsEl = this.querySelector(".sibling-qv-media-dots");
        this.titleEl = this.querySelector(".sibling-qv-title");
        this.priceEl = this.querySelector(".sibling-qv-price");
        this.reviewsEl = this.querySelector(".sibling-qv-reviews");
        this.colorLabelEl = this.querySelector(".sibling-qv-color-label");
        this.colorNameEl = this.querySelector(".sibling-qv-color-name");
        this.swatchesEl = this.querySelector(".sibling-qv-swatches");
        this.descEl = this.querySelector(".sibling-qv-description");
        this.specsEl = this.querySelector(".sibling-qv-specs");
        this.ctaEl = this.querySelector(".sibling-qv-cta");
        this.fromLabel = this.priceEl?.dataset.fromLabel || "";
        this.mediaCount = 0;

        this.boundOnSwatchClick = this.onSwatchClick.bind(this);
        this.swatchesEl?.addEventListener("click", this.boundOnSwatchClick);
      }

      show(opener) {
        const card = opener?.closest(".sibling-product");
        const dataEl = card?.querySelector("script.sibling-data");
        if (!dataEl) return;

        try {
          this.data = JSON.parse(dataEl.textContent);
        } catch (error) {
          return;
        }

        this.baseUrl = (this.data.url || "").split("?")[0].split("#")[0];

        const source = card.querySelector(".sibling-qv-source");
        this.injectSource(
          this.reviewsEl,
          source?.querySelector(".sibling-qv-src-reviews"),
        );
        this.injectSource(
          this.descEl,
          source?.querySelector(".sibling-qv-src-description"),
        );
        this.injectSource(
          this.specsEl,
          source?.querySelector(".sibling-qv-src-specs"),
        );

        const preselect = opener?.getAttribute("data-variant-id");
        this.renderContent(preselect);
        this.preloadVariantImages();

        super.show(opener);
      }

      preloadVariantImages() {
        if (!this.preloadedUrls) this.preloadedUrls = new Set();
        (this.data?.colors || []).forEach((color) => {
          const url = color?.image;
          if (!url || this.preloadedUrls.has(url)) return;
          this.preloadedUrls.add(url);
          const img = new Image();
          img.src = url;
        });
      }

      decodeImage(url) {
        return new Promise((resolve) => {
          if (!url) {
            resolve();
            return;
          }
          const img = new Image();
          img.src = url;
          if (typeof img.decode === "function") {
            img.decode().then(resolve, resolve);
          } else if (img.complete) {
            resolve();
          } else {
            img.onload = () => resolve();
            img.onerror = () => resolve();
          }
        });
      }

      injectSource(target, sourceNode) {
        if (!target) return;
        const html = sourceNode ? sourceNode.innerHTML.trim() : "";
        target.innerHTML = html;
        target.hidden = html === "";
      }

      renderContent(preselectId) {
        if (this.titleEl) this.titleEl.textContent = this.data.title || "";
        this.renderMedia();

        const colors = Array.isArray(this.data.colors) ? this.data.colors : [];

        if (!colors.length) {
          if (this.colorLabelEl) this.colorLabelEl.hidden = true;
          if (this.swatchesEl) this.swatchesEl.innerHTML = "";
          this.setPrice(this.data.defaultPrice);
          this.setCta(preselectId);
          return;
        }

        if (this.colorLabelEl) this.colorLabelEl.hidden = false;

        this.swatchesEl.innerHTML = colors
          .map((color) => {
            let swatchInner = "";
            if (color.swatchImage) {
              swatchInner = `<img src="${color.swatchImage}" alt="" loading="lazy">`;
            } else if (!color.swatchColor && color.thumb) {
              swatchInner = `<img src="${color.thumb}" alt="" loading="lazy">`;
            }
            const bgStyle =
              color.swatchColor && !color.swatchImage
                ? ` style="background:${color.swatchColor}"`
                : "";
            const label = this.escapeAttr(color.name || "");
            return `<li class="color-swatch"><button type="button" class="button--clear color sibling-qv-swatch" data-variant-id="${color.id}"${bgStyle} aria-label="${label}">${swatchInner}</button></li>`;
          })
          .join("");

        const matched = colors.find(
          (color) => String(color.id) === String(preselectId),
        );
        const selected = matched || colors[0];
        this.selectColor(selected.id);
      }

      onSwatchClick(event) {
        const button = event.target.closest(".sibling-qv-swatch");
        if (!button) return;
        this.selectColor(button.dataset.variantId, true);
      }

      selectColor(variantId, updateMedia = false) {
        const colors = this.data?.colors || [];
        const color = colors.find(
          (item) => String(item.id) === String(variantId),
        );
        if (!color) return;

        if (updateMedia) this.renderMedia(color);

        if (this.colorNameEl) this.colorNameEl.textContent = color.name || "";
        this.setPrice(color.price || this.data.defaultPrice);

        this.swatchesEl
          .querySelectorAll(".sibling-qv-swatch")
          .forEach((button) => {
            const isActive =
              String(button.dataset.variantId) === String(variantId);
            button
              .closest(".color-swatch")
              .classList.toggle("active", isActive);
          });

        this.setCta(variantId);
      }

      setPrice(price) {
        if (!this.priceEl) return;

        if (price) {
          const from = this.fromLabel
            ? `<span class="sibling-qv-from">${this.escapeAttr(this.fromLabel)}</span> `
            : "";
          this.priceEl.innerHTML = `${from}${price}`;
          this.priceEl.hidden = false;
        } else {
          this.priceEl.innerHTML = "";
          this.priceEl.hidden = true;
        }
      }

      async renderMedia(color) {
        if (!this.mediaEl) return;

        const token = (this.mediaRenderToken || 0) + 1;
        this.mediaRenderToken = token;

        const alt = this.escapeAttr(this.data?.title || "");
        const seen = new Set();
        const sources = [];

        if (color && color.image) {
          sources.push(color.image);
          if (color.imageId != null) seen.add(String(color.imageId));
        }

        (this.data?.images || []).forEach((item) => {
          if (item.url && !seen.has(String(item.id))) {
            sources.push(item.url);
            seen.add(String(item.id));
          }
        });

        if (!sources.length && this.data?.defaultImage) {
          sources.push(this.data.defaultImage);
        }

        if (color && color.image) {
          await this.decodeImage(color.image);
          if (token !== this.mediaRenderToken) return;
        }

        this.teardownMediaCarousel();

        this.mediaEl.scrollTop = 0;
        this.mediaEl.scrollLeft = 0;
        this.mediaEl.innerHTML = sources
          .map(
            (src) =>
              `<img class="sibling-qv-image" src="${src}" alt="${alt}" loading="lazy">`,
          )
          .join("");

        this.setupMediaCarousel(sources.length);
      }

      isMobileMedia() {
        return window.matchMedia("(max-width: 749px)").matches;
      }

      getMediaIndex() {
        if (!this.mediaEl || !this.mediaEl.children.length) return 0;
        const slideWidth = this.mediaEl.clientWidth;
        if (!slideWidth) return 0;
        return Math.round(this.mediaEl.scrollLeft / slideWidth);
      }

      scrollMediaTo(index) {
        if (!this.mediaEl) return;
        const slide = this.mediaEl.children[index];
        if (!slide) return;
        this.mediaEl.scrollTo({ left: slide.offsetLeft, behavior: "smooth" });
        this.setActiveMediaDot(index);
      }

      onMediaScroll() {
        this.setActiveMediaDot(this.getMediaIndex());
      }

      setActiveMediaDot(index) {
        this.mediaDotsEl
          ?.querySelectorAll(".sibling-qv-media-dot")
          .forEach((dot, dotIndex) => {
            dot.classList.toggle("is-active", dotIndex === index);
          });
      }

      setupMediaCarousel(count) {
        this.mediaCount = count;
        const showControls =
          count > 1 && this.isMobileMedia() && this.mediaWrapEl;

        if (this.mediaWrapEl) {
          this.mediaWrapEl.classList.toggle(
            "sibling-qv-media-wrap--carousel",
            showControls,
          );
        }

        if (!showControls) {
          if (this.mediaPrevBtn) this.mediaPrevBtn.hidden = true;
          if (this.mediaNextBtn) this.mediaNextBtn.hidden = true;
          if (this.mediaDotsEl) {
            this.mediaDotsEl.hidden = true;
            this.mediaDotsEl.innerHTML = "";
          }
          return;
        }

        if (this.mediaPrevBtn) this.mediaPrevBtn.hidden = false;
        if (this.mediaNextBtn) this.mediaNextBtn.hidden = false;
        if (this.mediaDotsEl) {
          this.mediaDotsEl.hidden = false;
          this.mediaDotsEl.innerHTML = Array.from({ length: count }, (_, i) => {
            const label = this.escapeAttr(String(i + 1));
            const activeClass = i === 0 ? " is-active" : "";
            return `<button type="button" class="sibling-qv-media-dot${activeClass}" data-index="${i}" aria-label="${label}"></button>`;
          }).join("");
        }

        this.boundOnMediaScroll = this.onMediaScroll.bind(this);
        this.mediaEl.addEventListener("scroll", this.boundOnMediaScroll, {
          passive: true,
        });

        this.boundOnDotClick = (event) => {
          const dot = event.target.closest(".sibling-qv-media-dot");
          if (!dot) return;
          this.scrollMediaTo(Number(dot.dataset.index));
        };
        this.mediaDotsEl?.addEventListener("click", this.boundOnDotClick);

        this.boundOnPrevClick = () => {
          this.scrollMediaTo(Math.max(0, this.getMediaIndex() - 1));
        };
        this.boundOnNextClick = () => {
          this.scrollMediaTo(
            Math.min(this.mediaCount - 1, this.getMediaIndex() + 1),
          );
        };
        this.mediaPrevBtn?.addEventListener("click", this.boundOnPrevClick);
        this.mediaNextBtn?.addEventListener("click", this.boundOnNextClick);
      }

      teardownMediaCarousel() {
        if (this.boundOnMediaScroll) {
          this.mediaEl?.removeEventListener("scroll", this.boundOnMediaScroll);
          this.boundOnMediaScroll = null;
        }
        if (this.boundOnDotClick) {
          this.mediaDotsEl?.removeEventListener("click", this.boundOnDotClick);
          this.boundOnDotClick = null;
        }
        if (this.boundOnPrevClick) {
          this.mediaPrevBtn?.removeEventListener("click", this.boundOnPrevClick);
          this.boundOnPrevClick = null;
        }
        if (this.boundOnNextClick) {
          this.mediaNextBtn?.removeEventListener("click", this.boundOnNextClick);
          this.boundOnNextClick = null;
        }
        if (this.mediaDotsEl) {
          this.mediaDotsEl.innerHTML = "";
          this.mediaDotsEl.hidden = true;
        }
        if (this.mediaPrevBtn) this.mediaPrevBtn.hidden = true;
        if (this.mediaNextBtn) this.mediaNextBtn.hidden = true;
        this.mediaWrapEl?.classList.remove("sibling-qv-media-wrap--carousel");
      }

      setCta(variantId) {
        if (!this.ctaEl || !this.baseUrl) return;

        this.ctaEl.href = variantId
          ? `${this.baseUrl}?variant=${variantId}`
          : this.baseUrl;
      }

      escapeAttr(value) {
        return String(value)
          .replaceAll("&", "&amp;")
          .replaceAll('"', "&quot;")
          .replaceAll("<", "&lt;")
          .replaceAll(">", "&gt;");
      }
    },
  );
}
